import { ImgID } from "../../../image_loader/img_id.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { KeyHuntService, type KeyHuntTask, type KeyHuntTaskStatus } from "../../../key_hunt/key_hunt_service.js";
import { taskMetricIconId } from "../../screens/daily_tasks_screen/daily_task_card.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, type UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import type { OfferRewardDto, OfferRewardType } from "../../../api/dto/offers.dto.js";
import { mysteryBoxRewardIcon } from "../../../offers/mystery_box_offer.js";
import { KeyHuntLocalLayout } from "./key_hunt_layout.js";

/**
 * One Key Hunt task (Figma "Component 59") on the Daily Tasks row art: icon, title,
 * progress or CLAIM, and the reward box with the key and the booster. A completed
 * row is a button; on the others the key and the booster name themselves on tap.
 */
export class KeyHuntTaskRow extends UIElement
{
  private readonly task: KeyHuntTask;
  private readonly status: KeyHuntTaskStatus;
  private key_image!: UIImage;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    task: KeyHuntTask,
    on_claim: ( task: KeyHuntTask ) => void
  )
  {
    const status = KeyHuntService.taskStatus( task );
    super(
      ctx,
      x,
      y,
      KeyHuntLocalLayout.widthFromDesign( 945 ),
      KeyHuntLocalLayout.heightFromDesign( 174 ),
      false,
      status === "completed"
    );

    this.task = task;
    this.status = status;

    if ( status === "completed" )
      this.setOnClick( () => on_claim( task ) );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createIcon();
    this.createTitle();

    if ( this.status === "completed" )
      this.createClaimText();
    else if ( this.status === "progress" )
      this.createProgress();

    this.createReward();
  }

  private createBackground(): void
  {
    const bg_id = this.status === "completed" ? ImgID.UI_DailyTasksTaskClaim
      : this.status === "claimed" ? ImgID.UI_DailyTasksTaskComplete
        : ImgID.UI_DailyTasksTaskNormal;

    const bg = new UIImage(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      bg_id
    );
    this.addVisual( bg );
  }

  private createIcon(): void
  {
    if ( this.status === "claimed" )
    {
      const confirm = new UIImage(
        this.ctx,
        KeyHuntLocalLayout.xFromDesign( 43 ),
        KeyHuntLocalLayout.yFromDesign( 39 ),
        KeyHuntLocalLayout.widthFromDesign( 100 ),
        KeyHuntLocalLayout.heightFromDesign( 92 ),
        ImgID.UI_DailyTasksProgressConfirm
      );
      this.addVisual( confirm );
      return;
    }

    // Figma shows the plain cannon for levels, not the Daily Tasks levels icon.
    const icon_id = this.task.metric === "complete_levels"
      ? ImgID.UI_DailyTasksTaskCannon
      : taskMetricIconId( this.task.metric );

    const icon = new UIImage(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 28 ),
      KeyHuntLocalLayout.yFromDesign( 20 ),
      KeyHuntLocalLayout.widthFromDesign( 130 ),
      KeyHuntLocalLayout.heightFromDesign( 130 ),
      icon_id
    );
    this.addVisual( icon );
  }

  private createTitle(): void
  {
    const title_y = this.status === "claimed" ? 54 : 21;

    const title = new UITextElement(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 180 ),
      KeyHuntLocalLayout.yFromDesign( title_y ),
      KeyHuntLocalLayout.widthFromDesign( 446 ),
      KeyHuntLocalLayout.heightFromDesign( 54 ),
      {
        localization_key: this.task.title_key,
        text_variable_params: [ String( this.task.target ) ],
        font_size: KeyHuntLocalLayout.textSizeFromDesign( 40 ),
        min_font_size: KeyHuntLocalLayout.textSizeFromDesign( 28 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#5E3D27",
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        scale_to_width: true,
        outline_width: KeyHuntLocalLayout.textBorderSizeFromDesign( 5 ),
        outline_color: "rgba(255, 255, 255, 0.61)",
        shadow_offset_x: 0,
        shadow_offset_y: 0
      }
    );
    this.addVisual( title );
  }

  private createClaimText(): void
  {
    const claim = new UITextElement(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 180 ),
      KeyHuntLocalLayout.yFromDesign( 69 ),
      KeyHuntLocalLayout.widthFromDesign( 300 ),
      KeyHuntLocalLayout.heightFromDesign( 84 ),
      {
        localization_key: "CLAIM",
        font_size: KeyHuntLocalLayout.textSizeFromDesign( 60 ),
        min_font_size: KeyHuntLocalLayout.textSizeFromDesign( 48 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        scale_to_width: true,
        outline_width: KeyHuntLocalLayout.textBorderSizeFromDesign( 12 ),
        outline_color: "#2C6B0F",
      }
    );
    this.addVisual( claim );
  }

  /** The track (446x50 with a 5px border) is part of the row bitmap; the fill sits 6px in. */
  private createProgress(): void
  {
    const share = Math.max( 0, Math.min( 1, this.task.progress / this.task.target ) );
    const fill_design_w = ( 446 - 12 ) * share;

    if ( fill_design_w > 0 )
    {
      const fill = new UINineSliceElement(
        this.ctx,
        KeyHuntLocalLayout.xFromDesign( 186 ),
        KeyHuntLocalLayout.yFromDesign( 90 ),
        KeyHuntLocalLayout.widthFromDesign( Math.max( 40, fill_design_w ) ),
        KeyHuntLocalLayout.heightFromDesign( 40 ),
        ImgID.UI_DailyTasksTaskProgressBar,
        "task_progress_bar",
        undefined,
        undefined,
        false,
        true
      );
      this.addVisual( fill );
    }

    const progress_text = new UITextElement(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 180 ),
      KeyHuntLocalLayout.yFromDesign( 84 ),
      KeyHuntLocalLayout.widthFromDesign( 446 ),
      KeyHuntLocalLayout.heightFromDesign( 50 ),
      {
        text: `${this.task.progress}/${this.task.target}`,
        font_size: KeyHuntLocalLayout.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_width: KeyHuntLocalLayout.textBorderSizeFromDesign( 5 ),
        outline_color: "rgba(10, 87, 88, 0.61)",
      }
    );
    this.addVisual( progress_text );
  }

  private createReward(): void
  {
    const reward = this.task.reward;

    const key = new UIImage(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 659 ),
      KeyHuntLocalLayout.yFromDesign( 41 ),
      KeyHuntLocalLayout.widthFromDesign( 86 ),
      KeyHuntLocalLayout.heightFromDesign( 94 ),
      ImgID.UI_KeyHuntKeyBig
    );
    this.key_image = key;

    const plus = new UITextElement(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 757 ),
      KeyHuntLocalLayout.yFromDesign( 59 ),
      KeyHuntLocalLayout.widthFromDesign( 48 ),
      KeyHuntLocalLayout.heightFromDesign( 50 ),
      this.rewardTextOptions( "+" )
    );

    const icon = new UIImage(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 806 ),
      KeyHuntLocalLayout.yFromDesign( 29 ),
      KeyHuntLocalLayout.widthFromDesign( 100 ),
      KeyHuntLocalLayout.heightFromDesign( 100 ),
      KeyHuntTaskRow.getRewardImgId( reward.type )
    );

    const amount = new UITextElement(
      this.ctx,
      KeyHuntLocalLayout.xFromDesign( 806 ),
      KeyHuntLocalLayout.yFromDesign( 95 ),
      KeyHuntLocalLayout.widthFromDesign( 100 ),
      KeyHuntLocalLayout.heightFromDesign( 53 ),
      this.rewardTextOptions( KeyHuntTaskRow.getRewardAmountText( reward ) )
    );

    if ( this.status === "claimed" )
    {
      key.setOpacityImmediate( 0.36 );
      plus.setOpacityImmediate( 0.5 );
      icon.setOpacityImmediate( 0.5 );
      amount.setOpacityImmediate( 0.5 );
    }

    this.addVisual( key );
    this.addVisual( plus );
    this.addVisual( icon );
    this.addVisual( amount );

    // On a completed row the whole card claims, so the names stay on the other rows.
    if ( this.status !== "completed" )
    {
      key.setClickable( true );
      icon.setClickable( true );
      RewardTooltip.attachName( key, () => LocalizationManager.t( "KEY_HUNT.KEY_NAME" ) );
      RewardTooltip.attach( icon, reward.type );
    }
  }

  private rewardTextOptions( text: string ): UITextOptions
  {
    return {
      text,
      font_size: KeyHuntLocalLayout.textSizeFromDesign( 38 ),
      min_font_size: KeyHuntLocalLayout.textSizeFromDesign( 26 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      fit_font_size: true,
      scale_to_width: true,
      outline_width: KeyHuntLocalLayout.textBorderSizeFromDesign( 9 ),
      outline_color: "#003F87",
    };
  }

  /** The row's key: the claim animation flies a copy of it into the key bar. */
  public getKeyImage(): UIImage
  {
    return this.key_image;
  }

  /** Figma draws the coin pile and the 1+1 heart here; the rest as in the Mystery Box. */
  private static getRewardImgId( type: OfferRewardType ): ImgID
  {
    if ( type === "coins" ) return ImgID.UI_OnePlusOneOfferCoins;
    if ( type === "infinite_hearts_minutes" ) return ImgID.UI_OnePlusOneOfferInfiniteHeart;
    return mysteryBoxRewardIcon( type );
  }

  /** "x3", "300", "1h". */
  private static getRewardAmountText( reward: OfferRewardDto ): string
  {
    if ( reward.type === "coins" ) return String( reward.amount );
    if ( reward.type === "infinite_hearts_minutes" || reward.type === "no_ads_minutes" )
    {
      const hours = reward.amount / 60;
      return hours % 1 === 0 ? `${hours}h` : `${reward.amount}m`;
    }
    return `x${reward.amount}`;
  }

  /** Row decorations never take touches themselves; the row (or a tooltip target) does. */
  private addVisual( element: UIElement ): void
  {
    element.setClickable( false );
    this.addChild( element );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "KeyHuntTaskRow";
  }
}
